/**
 * Field-ownership merge strategy for the shared session record.
 *
 * The meeting record is written concurrently by several deepstream clients
 * (agent, consumer, agent-preview). Deepstream versions the record optimistically
 * PER RECORD, so concurrent writes conflict (INVALID_VERSION). The built-in
 * strategies are all-or-nothing: REMOTE_WINS discards the local write, LOCAL_WINS
 * discards the remote one — either way an update is lost, which desyncs the
 * agent/preview/consumer.
 *
 * `ownershipMergeStrategy(ownedPaths)` resolves a conflict by starting from the
 * REMOTE value (authoritative for everyone else's fields) and re-applying ONLY
 * the paths this client owns from its LOCAL value. Deepstream then writes the
 * merged result back to the server (only if it differs from remote) and
 * converges. Result: no owned update is ever lost, and no other client's update
 * is clobbered — as long as ownership is disjoint across clients.
 *
 * @param {string[]} ownedPaths dot-notation paths this client is the writer of
 *   (e.g. 'sync.currentInteraction')
 * @returns deepstream MergeStrategy: (localValue, localVersion, remoteValue, remoteVersion, callback)
 */
export function ownershipMergeStrategy(ownedPaths) {
  return (localValue, localVersion, remoteValue, remoteVersion, callback) => {
    try {
      // Start from remote (keeps every other client's latest fields)
      const merged =
        remoteValue !== null && remoteValue !== undefined
          ? JSON.parse(JSON.stringify(remoteValue))
          : {}

      // Re-apply this client's owned fields from local so they are never lost.
      // Only when the local record actually has the field (undefined = absent).
      for (const path of ownedPaths) {
        const localFieldValue = getByPath(localValue, path)
        if (localFieldValue !== undefined) {
          setByPath(merged, path, localFieldValue)
        }
      }

      callback(null, merged)
    } catch (e) {
      // Any failure → safe fallback to remote-wins (current behaviour)
      callback(null, remoteValue)
    }
  }
}

/** Read a dot-notation path from an object. Returns undefined if any segment is missing. */
export function getByPath(obj, path) {
  if (obj === null || obj === undefined) return undefined
  const segments = path.split('.')
  let current = obj
  for (const segment of segments) {
    if (current === null || current === undefined) return undefined
    current = current[segment]
  }
  return current
}

/** Write a dot-notation path into an object, creating intermediate objects as needed. */
export function setByPath(obj, path, value) {
  const segments = path.split('.')
  let current = obj
  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i]
    if (
      current[segment] === null ||
      current[segment] === undefined ||
      typeof current[segment] !== 'object'
    ) {
      current[segment] = {}
    }
    current = current[segment]
  }
  current[segments[segments.length - 1]] = value
  return obj
}
